import { fetchEnrichedCustomerData } from '@Services/init/partnerWelcome.api.ts';
import { processFieldValidationErrors } from '@Services/errors/processFieldValidationErrors.ts';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { router } from '@Routes/__root';
import { CustomerDataSchema } from '@Store/index';
import { MAIN_INCOME_SOURCE } from '@Types/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { queryClient } from '../../queryClient';
import {
  cddRoutes,
  eddNonProfitRoutes,
  getCountryEddRoutes,
  applyCountryEddToStack,
} from '@Consts/navigation-stack-map';
import type { CustomerData } from '@Store/index';
import type { CountryApiResponse } from '@Types/country';

const isStepNameValid = (stepName: string): stepName is keyof typeof routesModel => {
  return stepName in routesModel;
};

export const applyCountryEddToNavigationStack = () => {
  const idCountry = useBoundStore.getState().customer.customerData.idCountry;
  if (!idCountry) return;

  const countriesData = queryClient.getQueryData<CountryApiResponse>(['countries', true, false]);
  const countries = countriesData?.countries ?? [];
  const country = countries.find((c) => c.code === idCountry);
  const countryRoutes = getCountryEddRoutes(country);

  const { navigationStack, setNavigationStack } = useBoundStore.getState().onboardingSlice;
  setNavigationStack(applyCountryEddToStack(navigationStack, countryRoutes));
};

export const reArrangeNavigationStack = async (stepName: string) => {
  const { getCustomerData } = useBoundStore.getState();

  const navigationStack = useBoundStore.getState().onboardingSlice.navigationStack;
  const setPickupWhereLeftNavigationStack =
    useBoundStore.getState().onboardingSlice.setPickupWhereLeftNavigationStack;

  const index = navigationStack.findIndex((step) => step === stepName);
  if (index !== -1 && getCustomerData().customerId) {
    const currentData = getCustomerData();
    const enrichedCustomerData = await fetchEnrichedCustomerData();
    if (enrichedCustomerData) {
      updateCustomerDataFromServer(enrichedCustomerData, currentData);
      processFieldValidationErrors(enrichedCustomerData.fieldValidationErrors);
    }
  }

  const isWhiteGlove = useBoundStore.getState().onboardingSlice.getIsWhiteGloveFlow();
  const incomeSource = getCustomerData().customerData.customerBusinessData.mainIncomeSource;
  if (
    !isWhiteGlove &&
    (incomeSource == MAIN_INCOME_SOURCE.SOLE_PROP_NO_DBA ||
      incomeSource == MAIN_INCOME_SOURCE.SOLE_PROP_DBA ||
      incomeSource == MAIN_INCOME_SOURCE.NON_PROFIT)
  ) {
    adjustNavigationStackAccordingToIncomeSource(incomeSource);
  }

  applyCountryEddToNavigationStack();

  const navigationStackUpdated = useBoundStore.getState().onboardingSlice.navigationStack;

  if (!isStepNameValid(stepName)) {
    router.navigate({ to: routesModel.WELCOME.path });
    return;
  }
  setPickupWhereLeftNavigationStack(navigationStackUpdated);

  // Recompute the index against the updated stack: the income-source/non-profit and
  // country-EDD adjustments above can remove or insert routes before `stepName`, which
  // makes the index captured from the pre-adjustment stack stale (and can go out of bounds).
  const updatedIndex = navigationStackUpdated.findIndex((step) => step === stepName);
  const routeIndex = updatedIndex === -1 ? 0 : updatedIndex;
  const route = navigationStackUpdated[routeIndex];
  if (!route) {
    router.navigate({ to: routesModel.WELCOME.path });
    return;
  }
  router.navigate({ to: routesModel[route].path });
};

export const updateCustomerDataFromServer = (
  enrichedCustomerData: Partial<CustomerData>,
  currentData: CustomerData
) => {
  const { setCustomerData } = useBoundStore.getState();
  const businessType = enrichedCustomerData.business?.businessType;
  if (businessType && !enrichedCustomerData.customerData?.customerBusinessData?.mainIncomeSource) {
    enrichedCustomerData = {
      ...enrichedCustomerData,
      customerData: {
        ...enrichedCustomerData.customerData,
        customerBusinessData: {
          ...enrichedCustomerData.customerData?.customerBusinessData,
          mainIncomeSource: businessType,
        },
      } as CustomerData['customerData'],
    };
  }
  const parsedEnrichedData = CustomerDataSchema.partial().parse(enrichedCustomerData);
  const mergedData = {
    ...currentData,
    ...parsedEnrichedData,
    customerData: {
      ...currentData.customerData,
      ...parsedEnrichedData.customerData,
    },
  };
  setCustomerData({ ...mergedData } as CustomerData);
};

export const adjustNavigationStackAccordingToIncomeSource = (
  mainIncomeSource: MAIN_INCOME_SOURCE
) => {
  const {
    onboardingSlice: { setNavigationStack, navigationStack },
  } = useBoundStore.getState();

  if (mainIncomeSource === MAIN_INCOME_SOURCE.NON_PROFIT) {
    const routesToRemove = [RoutesMap.OWNER_CENTER, RoutesMap.EDD_INTENDED_USE, ...cddRoutes];
    const navigationStackToUpdate = navigationStack.filter(
      (route) => !routesToRemove.includes(route)
    );
    const index = navigationStackToUpdate.indexOf(RoutesMap.BUSINESS_ADDRESS);
    navigationStackToUpdate.splice(index + 1, 0, ...eddNonProfitRoutes);
    setNavigationStack(navigationStackToUpdate);
  } else if (mainIncomeSource === MAIN_INCOME_SOURCE.SOLE_PROP_DBA) {
    if (!navigationStack.includes(RoutesMap.KNOW_YOUR_BUSINESS)) {
      const index = navigationStack.indexOf(RoutesMap.INDUSTRY);
      navigationStack.splice(index + 1, 0, RoutesMap.KNOW_YOUR_BUSINESS);
    }
    const routesToRemove = [
      RoutesMap.OWNER_CENTER,
      RoutesMap.REGISTERED_ADDRESS,
      RoutesMap.BUSINESS_ADDRESS,
      RoutesMap.MAILING_ADDRESS,
    ];
    const navigationStackToUpdate = navigationStack.filter(
      (route) => !routesToRemove.includes(route)
    );

    setNavigationStack(navigationStackToUpdate);
  } else if (mainIncomeSource === MAIN_INCOME_SOURCE.SOLE_PROP_NO_DBA) {
    const routesToRemove = [
      RoutesMap.OWNER_CENTER,
      RoutesMap.KNOW_YOUR_BUSINESS,
      RoutesMap.REGISTERED_ADDRESS,
      RoutesMap.BUSINESS_ADDRESS,
      RoutesMap.MAILING_ADDRESS,
    ];
    const navigationStackToUpdate = navigationStack.filter(
      (route) => !routesToRemove.includes(route)
    );
    setNavigationStack(navigationStackToUpdate);
  }
};
